/**
 * HIPAA-Compliant User Activity Hook
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, scroll, touch)
 * - 15-minute inactivity timeout (HIPAA §164.312(a)(2)(iii))
 * - 2-minute warning before forced logout
 * - Extend session option
 * - Forced logout and redirect to session expiry page
 * 
 * Based on HIPAA_COMPLIANCE_COMPLETE_SECURITY_GUIDE.md
 * Frontend guide lines 1064-1132
 */

import { useState, useEffect, useCallback } from 'react';

// HIPAA-recommended timeout: 15 minutes
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_TIME = 2 * 60 * 1000;        // 2 minutes warning

/**
 * Hook to track user activity and enforce HIPAA timeout
 * 
 * @returns {object} {
 *   showWarning: boolean - Whether to show timeout warning
 *   extendSession: function - Manually extend session
 *   timeRemaining: number - Milliseconds until timeout
 *   minutesRemaining: number - Minutes until timeout
 * }
 */
export const useUserActivity = () => {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  
  // Reset timer on activity
  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);
  
  useEffect(() => {
    // Events that count as "activity"
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners for all activity events
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });
    
    // Check for timeout every 30 seconds
    const interval = setInterval(() => {
      const now = Date.now();
      const inactiveTime = now - lastActivity;
      
      if (inactiveTime > INACTIVITY_TIMEOUT) {
        // Timeout reached - force logout
        console.log('HIPAA: Session timeout due to inactivity (15 minutes)');
        handleTimeout();
      } else if (inactiveTime > INACTIVITY_TIMEOUT - WARNING_TIME) {
        // Show warning (2 minutes before timeout)
        if (!showWarning) {
          console.log('HIPAA: Showing session timeout warning (2 minutes remaining)');
          setShowWarning(true);
        }
      }
    }, 30000); // Check every 30 seconds
    
    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      clearInterval(interval);
    };
  }, [lastActivity, resetTimer, showWarning]);
  
  /**
   * Handle timeout - force logout and redirect
   */
  const handleTimeout = () => {
    console.log('HIPAA: Forcing logout due to session timeout');
    
    // Clear all tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('csrfToken');
    
    // Redirect to HIPAA session expiry page
    window.location.href = '/hipaa-session-expiry';
  };
  
  /**
   * Extend session - reset activity timer
   */
  const extendSession = useCallback(() => {
    console.log('HIPAA: Session extended by user');
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);
  
  // Calculate time remaining
  const timeRemaining = INACTIVITY_TIMEOUT - (Date.now() - lastActivity);
  const minutesRemaining = Math.max(0, Math.floor(timeRemaining / 60000));
  const secondsRemaining = Math.max(0, Math.floor((timeRemaining % 60000) / 1000));
  
  return {
    showWarning,
    extendSession,
    timeRemaining,
    minutesRemaining,
    secondsRemaining
  };
};

export default useUserActivity;

